<template>
    <div class="camp-refund-section" v-if="refunds && refunds.length">
        <hr class="camp-order-hr" />
        <div class="camp-form-section-title">退費申請狀態</div>

        <template v-for="r in orderedRefunds">
        <div v-if="r.Id === firstHistoryId" :key="'history-toggle-' + r.Id" class="mb-2">
            <b-button variant="link" size="sm" class="p-0" @click="historyOpen = !historyOpen">
                <b-icon :icon="historyOpen ? 'chevron-up' : 'chevron-down'"></b-icon>
                {{ historyOpen ? '收合退費歷程' : '退費歷程（' + historyCount + ' 筆）' }}
            </b-button>
        </div>
        <div :id="'refund-' + r.Id" :key="r.Id" v-show="isFeatured(r) || historyOpen" class="camp-refund-card camp-scroll-target">
            <!-- 歷程列：一行摘要，點擊展開完整內容 -->
            <div v-if="!isFeatured(r)" class="d-flex flex-wrap justify-content-between align-items-center camp-refund-history-summary"
                role="button" @click="toggleHistoryRow(r.Id)">
                <span>
                    <b-badge :variant="statusVariant(r.StatusNum)" class="mr-1">{{ r.Status }}</b-badge>
                    <span v-if="r.Amount != null" class="font-weight-bold">{{ r.Amount | money(0, 2) }} 元</span>
                    <span v-else class="small text-muted">金額待承辦回覆</span>
                </span>
                <span class="small text-muted">
                    {{ r.RefundMethod }}｜{{ r.Create | datefmt('YYYY-MM-DD HH:mm') }}
                    <b-icon :icon="historyExpanded[r.Id] ? 'chevron-up' : 'chevron-down'" class="ml-1"></b-icon>
                </span>
            </div>
            <div v-show="isFeatured(r) || historyExpanded[r.Id]">
            <div v-if="isFeatured(r)" class="d-flex flex-wrap justify-content-between align-items-start">
                <div>
                    <b-badge :variant="statusVariant(r.StatusNum)" class="mr-1">{{ r.Status }}</b-badge>
                    <span class="small text-muted">{{ r.RefundMethod }}｜申請於 {{ r.Create | datefmt('YYYY-MM-DD HH:mm') }}</span>
                </div>
                <div class="text-right">
                    <div v-if="r.Amount != null" class="font-weight-bold">{{ r.Amount | money(0, 2) }} 元</div>
                    <div v-else class="small text-muted">金額待承辦回覆</div>
                </div>
            </div>
            <div v-if="r.Reason" class="small mt-1"><span class="text-muted">申請原因：</span>{{ r.Reason }}</div>
            <div v-if="r.Memo" class="small mt-1"><span class="text-muted">承辦說明：</span>{{ r.Memo }}</div>

            <!-- 申請中：可撤回 -->
            <div v-if="r.StatusNum === 10" class="text-right mt-2">
                <b-button size="sm" variant="outline-secondary" :disabled="busyId === r.Id" @click="withdraw(r)">
                    撤回申請
                </b-button>
            </div>

            <!-- 轉帳退費且承辦已回覆：補帳戶資料 -->
            <div v-if="r.NeedRefundAccount" :id="'refund-account-' + r.Id"
                class="camp-refund-account-form camp-scroll-target mt-2">
                <div class="small text-muted mb-2">此為轉帳退費，請填寫收款帳戶並上傳存摺／帳戶證明後送出（送出後不可自行修改）。</div>
                <b-form-row>
                    <b-col cols="12" sm="3">
                        <b-form-group label="銀行" label-class="small">
                            <b-form-input size="sm" v-model="accountForms[r.Id].RefundBank" />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" sm="3">
                        <b-form-group label="分行" label-class="small">
                            <b-form-input size="sm" v-model="accountForms[r.Id].RefundBankBranch" />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" sm="3">
                        <b-form-group label="帳號" label-class="small">
                            <b-form-input size="sm" v-model="accountForms[r.Id].RefundAccount" inputmode="numeric" />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" sm="3">
                        <b-form-group label="戶名" label-class="small">
                            <b-form-input size="sm" v-model="accountForms[r.Id].RefundAccountName" />
                        </b-form-group>
                    </b-col>
                </b-form-row>
                <b-form-group label="帳戶證明圖片" label-class="small" class="mb-2">
                    <b-form-file size="sm" accept="image/jpeg,image/png,image/webp" browse-text="選擇檔案"
                        placeholder="未選擇檔案" :disabled="proofUploadingId === r.Id"
                        v-model="proofFiles[r.Id]" @input="(f) => onProofSelected(r, f)" />
                    <div v-if="proofUploadingId === r.Id" class="small text-muted mt-1">
                        <b-spinner small></b-spinner> 上傳中...
                    </div>
                    <div v-else-if="r.HasRefundAccountProofImage" class="small text-success mt-1">
                        <b-icon icon="check-circle-fill"></b-icon> 已上傳帳戶證明
                        <b-button size="sm" variant="link" class="p-0 ml-1" @click="viewProof(r)">檢視</b-button>
                    </div>
                </b-form-group>
                <div class="text-right">
                    <b-button size="sm" variant="primary" :disabled="!canSubmitAccount(r) || busyId === r.Id"
                        @click="submitAccount(r)">
                        送出轉帳資料
                    </b-button>
                </div>
            </div>

            <!-- 已送出轉帳資料：唯讀顯示 -->
            <div v-else-if="r.RefundMethodNum === 2 && [30, 40].includes(Number(r.StatusNum))" class="small mt-2 text-muted">
                收款帳戶：{{ [r.RefundBank, r.RefundBankBranch, r.RefundAccount, r.RefundAccountName].filter(Boolean).join(' / ') || '已提供' }}
                <b-button v-if="r.HasRefundAccountProofImage" size="sm" variant="link" class="p-0 ml-1"
                    @click="viewProof(r)">檢視證明</b-button>
            </div>
            </div>
        </div>
        </template>

        <!-- 證明圖片檢視 -->
        <b-modal id="modal-camp-refund-proof-view" title="帳戶證明" size="lg" hide-footer @hidden="clearProofView">
            <div class="text-center">
                <b-img v-if="proofViewUrl" :src="proofViewUrl" fluid></b-img>
                <div v-else class="text-muted py-4">讀取中...</div>
            </div>
        </b-modal>
    </div>
</template>
<script>
import {
    apiCampRefundWithdraw,
    apiCampRefundAccount,
    apiCampRefundProofUpload,
    apiCampRefundProofImage,
} from '@/api/API.js';
import { isActiveCampRefundStatus } from '@/utils/campEnums.js';

export default {
    name: 'CampRefundSection',
    props: {
        refunds: { type: Array, default: () => [] },
    },
    data() {
        return {
            accountForms: {},
            proofFiles: {},
            proofUploadingId: null,
            busyId: null,
            proofViewUrl: null,
            historyOpen: false,
            historyExpanded: {},
        };
    },
    computed: {
        // 以 Create 由新到舊判斷最新一筆，不依賴傳入順序。
        sortedRefunds() {
            return [...(this.refunds || [])].sort((a, b) => new Date(b.Create) - new Date(a.Create));
        },
        activeRefunds() {
            return this.sortedRefunds.filter((r) => isActiveCampRefundStatus(r.StatusNum));
        },
        // 常駐 = 所有尚未結束的退費；沒有進行中退費時，保留最新結果。其餘才收進歷程。
        featuredRefunds() {
            return this.activeRefunds.length ? this.activeRefunds : this.sortedRefunds.slice(0, 1);
        },
        historyRefunds() {
            const featuredIds = new Set(this.featuredRefunds.map((r) => r.Id));
            return this.sortedRefunds.filter((r) => !featuredIds.has(r.Id));
        },
        orderedRefunds() {
            return [...this.featuredRefunds, ...this.historyRefunds];
        },
        historyCount() {
            return this.historyRefunds.length;
        },
        firstHistoryId() {
            return this.historyRefunds.length ? this.historyRefunds[0].Id : null;
        },
    },
    watch: {
        refunds: {
            immediate: true,
            handler(list) {
                (list || []).forEach((r) => {
                    if (!this.accountForms[r.Id]) {
                        this.$set(this.accountForms, r.Id, {
                            RefundBank: r.RefundBank || '',
                            RefundBankBranch: r.RefundBankBranch || '',
                            RefundAccount: r.RefundAccount || '',
                            RefundAccountName: r.RefundAccountName || '',
                        });
                    }
                });
            },
        },
    },
    methods: {
        isFeatured(r) {
            return this.featuredRefunds.some((item) => item.Id === r.Id);
        },
        toggleHistoryRow(id) {
            this.$set(this.historyExpanded, id, !this.historyExpanded[id]);
        },
        statusVariant(statusNum) {
            const map = { 10: 'primary', 20: 'warning', 30: 'info', 40: 'success', 50: 'secondary', 60: 'danger' };
            return map[statusNum] || 'light';
        },
        canSubmitAccount(r) {
            const f = this.accountForms[r.Id] || {};
            return !!(f.RefundBank && f.RefundBankBranch && f.RefundAccount && f.RefundAccountName && r.HasRefundAccountProofImage);
        },
        withdraw(r) {
            if (!window.confirm('確定要撤回這筆退費申請嗎？撤回後報名會恢復原狀。')) return;
            this.busyId = r.Id;
            apiCampRefundWithdraw(r.Id, { Memo: '' })
                .then(() => {
                    this.busyId = null;
                    this.$emit('changed');
                })
                .catch((err) => {
                    this.busyId = null;
                    alert((err && err.response && err.response.data && err.response.data.error) || '撤回失敗，請稍後再試');
                });
        },
        onProofSelected(r, file) {
            if (!file) return;
            if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
                alert('帳戶證明僅接受 jpg、png、webp 格式');
                this.$set(this.proofFiles, r.Id, null);
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                alert('帳戶證明大小不可超過 5MB');
                this.$set(this.proofFiles, r.Id, null);
                return;
            }
            const fd = new FormData();
            fd.append('file', file);
            this.proofUploadingId = r.Id;
            apiCampRefundProofUpload(r.Id, fd)
                .then(() => {
                    this.proofUploadingId = null;
                    this.$emit('changed');
                })
                .catch((err) => {
                    this.proofUploadingId = null;
                    this.$set(this.proofFiles, r.Id, null);
                    alert((err && err.response && err.response.data && err.response.data.error) || '上傳失敗，請稍後再試');
                });
        },
        submitAccount(r) {
            const f = this.accountForms[r.Id] || {};
            if (!this.canSubmitAccount(r)) return;
            this.busyId = r.Id;
            apiCampRefundAccount(r.Id, {
                RefundBank: f.RefundBank.trim(),
                RefundBankBranch: f.RefundBankBranch.trim(),
                RefundAccount: f.RefundAccount.trim(),
                RefundAccountName: f.RefundAccountName.trim(),
            })
                .then(() => {
                    this.busyId = null;
                    this.$emit('changed');
                })
                .catch((err) => {
                    this.busyId = null;
                    alert((err && err.response && err.response.data && err.response.data.error) || '送出失敗，請稍後再試');
                });
        },
        viewProof(r) {
            this.clearProofView();
            this.$bvModal.show('modal-camp-refund-proof-view');
            apiCampRefundProofImage(r.Id)
                .then((res) => {
                    this.proofViewUrl = URL.createObjectURL(res.data);
                })
                .catch(() => {
                    this.$bvModal.hide('modal-camp-refund-proof-view');
                });
        },
        clearProofView() {
            if (this.proofViewUrl) {
                URL.revokeObjectURL(this.proofViewUrl);
                this.proofViewUrl = null;
            }
        },
    },
};
</script>
<style>
.camp-refund-section .camp-refund-card {
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 8px;
    background: #fff;
}

.camp-refund-section .camp-refund-card:not(:last-child) {
    margin-bottom: 10px;
}

.camp-refund-section .camp-refund-history-summary {
    cursor: pointer;
}

.camp-refund-section .camp-refund-account-form {
    border-top: 1px dashed #dee2e6;
    padding-top: 8px;
}

.camp-refund-section .camp-scroll-target {
    scroll-margin-top: 1rem;
}

@media (max-width: 767.98px) {
    .camp-refund-section .camp-refund-card {
        padding: 12px;
    }

    .camp-refund-section .camp-refund-history-summary > span {
        width: 100%;
    }

    .camp-refund-section .camp-refund-history-summary > span + span {
        margin-top: 4px;
    }
}
</style>
